/** @type {import('tailwindcss').Config} */
const mixedWithTransparent = (variableName) =>
    `color-mix(in srgb, var(${variableName}), transparent calc(100% - 100% * <alpha-value>))`;

module.exports = {
    darkMode: ["selector", '[data-theme="dark"]'],
    content: [
        "./src/common/components/**/*.{js,ts,jsx,tsx}",
        "./src/common/hooks/**/*.{js,ts,jsx,tsx}",
        "./src/common/services/**/*.{js,ts,jsx,tsx}",
        "./src/features/**/components/**/*.{js,ts,jsx,tsx}",
        "./src/pages/**/*.{js,ts,jsx,tsx}",
    ],
    theme: {
        screens: {
            sm: "480px",
            md: "768px",
            lg: "976px",
            xl: "1440px",
        },
        fontFamily: {
            sans: ["Graphik", "sans-serif"], // TODO
            serif: ["Merriweather", "serif"], // TODO
        },
        fontSize: {
            xs: "0.75rem",
            sm: "0.8rem",
            base: "1rem" /* 14px */,
            lg: "1.143rem" /* 16px */,
            xl: "1.25rem",
            "2xl": "1.563rem",
            "3xl": "1.953rem",
            "4xl": "2.441rem",
            "5xl": "3.052rem",
            "6xl": "3.491rem",
            "7xl": "3.987rem",
            "8xl": "4.482rem",
        },
        extend: {
            maxWidth: {
                "1/10": "10%",
                "1/5": "20%",
                "1/4": "25%",
                "3/10": "30%",
                "1/3": "33.33%",
                "2/5": "40%",
                "1/2": "50%",
                "3/5": "60%",
                "2/3": "66.67%",
                "3/4": "75%",
                "7/10": "70%",
                "4/5": "80%",
                "9/10": "90%",
            },
            boxShadow: {
                heading:
                    "0px 2px 4px -1px rgba(15, 23, 42, 0.03), 0px 0px 1px rgba(15, 23, 42, 0.03), 0px 4px 6px -1px rgba(15, 23, 74, 0.05)",
                "bottom-large":
                    "0px 0px 1px rgba(15, 23, 42, 0.06), 0px 20px 25px -5px rgba(15, 23, 42, 0.1), 0px 10px 10px -5px rgba(15, 23, 74, 0.1)",
                center: "0px 2px 1px -1px #00000000, 0px 1px 3px 0px #00000033, 0px 1px 5px 0px #00000040",
                card: "0 2px 1px -1px rgba(0, 0, 0, 0.2), 0 1px 1px 0 rgba(0, 0, 0, 0.14), 0 1px 3px 0 rgba(0, 0, 0, 0.12)",
            },
            spacing: {
                1: "0.25rem",
                2: "0.5rem",
                4: "1rem",
                6: "1.5rem",
                8: "2rem",
                10: "2.5rem",
                12: "3rem",
                16: "4rem",
            },
            fontFamily: {
                headline: ["Maven Pro", "Inter", "sans-serif"],
                text: ["Roboto", "Inter", "sans-serif"],
                code: ["Fixedsys", "monospace"],
            },
            colors: {
                default:
                    "color-mix(in srgb, var(--text-color), transparent calc(100% - 100% * <alpha-value>))", // TODO: find better name
                primary: {
                    default:
                        "color-mix(in srgb, var(--primary-color), transparent calc(100% - 100% * <alpha-value>))",
                    text: "var(--primary-color-text)",
                    dark: {
                        10: "var(--primary-color-dark-10)",
                        20: "var(--primary-color-dark-20)",
                        30: "var(--primary-color-dark-30)",
                        40: "var(--primary-color-dark-40)",
                        50: "var(--primary-color-dark-50)",
                        60: "var(--primary-color-dark-60)",
                        70: "var(--primary-color-dark-70)",
                        80: "var(--primary-color-dark-80)",
                        90: "var(--primary-color-dark-90)",
                    },
                    light: {
                        10: "var(--primary-color-light-10)",
                        20: "var(--primary-color-light-20)",
                        30: "var(--primary-color-light-30)",
                        40: "var(--primary-color-light-40)",
                        50: "var(--primary-color-light-50)",
                        60: "var(--primary-color-light-60)",
                        70: "var(--primary-color-light-70)",
                        80: "var(--primary-color-light-80)",
                        90: "var(--primary-color-light-90)",
                    },
                },
                secondary: {
                    default:
                        "color-mix(in srgb, var(--secondary-color), transparent calc(100% - 100% * <alpha-value>))", // to enable opacity, see: https://github.com/tailwindlabs/tailwindcss/discussions/12622#discussioncomment-7909796
                    text: "var(--secondary-color-text)",
                    dark: {
                        10: "var(--secondary-color-dark-10)",
                        20: "var(--secondary-color-dark-20)",
                        30: "var(--secondary-color-dark-30)",
                        40: "var(--secondary-color-dark-40)",
                        50: "var(--secondary-color-dark-50)",
                        60: "var(--secondary-color-dark-60)",
                        70: "var(--secondary-color-dark-70)",
                        80: "var(--secondary-color-dark-80)",
                        90: "var(--secondary-color-dark-90)",
                    },
                    light: {
                        10: "var(--secondary-color-light-10)",
                        20: "var(--secondary-color-light-20)",
                        30: "var(--secondary-color-light-30)",
                        40: "var(--secondary-color-light-40)",
                        50: "var(--secondary-color-light-50)",
                        60: "var(--secondary-color-light-60)",
                        70: "var(--secondary-color-light-70)",
                        80: "var(--secondary-color-light-80)",
                        90: "var(--secondary-color-light-90)",
                    },
                },
                accent: {
                    default: "var(--accent-color)", // Base accent color
                    dark: {
                        10: "var(--accent-color-dark-10)",
                        20: "var(--accent-color-dark-20)",
                        30: "var(--accent-color-dark-30)",
                        40: "var(--accent-color-dark-40)",
                        50: "var(--accent-color-dark-50)",
                        60: "var(--accent-color-dark-60)",
                        70: "var(--accent-color-dark-70)",
                        80: "var(--accent-color-dark-80)",
                        90: "var(--accent-color-dark-90)",
                    },
                    light: {
                        10: "var(--accent-color-light-10)",
                        20: "var(--accent-color-light-20)",
                        30: "var(--accent-color-light-30)",
                        40: "var(--accent-color-light-40)",
                        50: "var(--accent-color-light-50)",
                        60: "var(--accent-color-light-60)",
                        70: "var(--accent-color-light-70)",
                        80: "var(--accent-color-light-80)",
                        90: "var(--accent-color-light-90)",
                    },
                },
                error: {
                    default:
                        "color-mix(in srgb, var(--error-color), transparent calc(100% - 100% * <alpha-value>))", // to enable opacity, see: https://github.com/tailwindlabs/tailwindcss/discussions/12622#discussioncomment-7909796
                    text: "var(--error-color-text)",
                    dark: {
                        10: "var(--error-color-dark-10)",
                        20: "var(--error-color-dark-20)",
                        30: "var(--error-color-dark-30)",
                        40: "var(--error-color-dark-40)",
                        50: "var(--error-color-dark-50)",
                        60: "var(--error-color-dark-60)",
                        70: "var(--error-color-dark-70)",
                        80: "var(--error-color-dark-80)",
                        90: "var(--error-color-dark-90)",
                    },
                    light: {
                        10: "var(--error-color-light-10)",
                        20: "var(--error-color-light-20)",
                        30: "var(--error-color-light-30)",
                        40: "var(--error-color-light-40)",
                        50: "var(--error-color-light-50)",
                        60: "var(--error-color-light-60)",
                        70: "var(--error-color-light-70)",
                        80: "var(--error-color-light-80)",
                        90: "var(--error-color-light-90)",
                        93: "var(--error-color-light-93)", // Error background color
                    },
                },
                warning: {
                    default:
                        "color-mix(in srgb, var(--warning-color), transparent calc(100% - 100% * <alpha-value>))", // to enable opacity, see: https://github.com/tailwindlabs/tailwindcss/discussions/12622#discussioncomment-7909796
                    text: "var(--warning-color-text)",
                    dark: {
                        10: "var(--warning-color-dark-10)",
                        20: "var(--warning-color-dark-20)",
                        30: "var(--warning-color-dark-30)",
                        40: "var(--warning-color-dark-40)",
                        50: "var(--warning-color-dark-50)",
                        60: "var(--warning-color-dark-60)",
                        70: "var(--warning-color-dark-70)",
                        80: "var(--warning-color-dark-80)",
                        90: "var(--warning-color-dark-90)",
                    },
                    light: {
                        10: "var(--warning-color-light-10)",
                        20: "var(--warning-color-light-20)",
                        30: "var(--warning-color-light-30)",
                        40: "var(--warning-color-light-40)",
                        50: "var(--warning-color-light-50)",
                        60: "var(--warning-color-light-60)",
                        70: "var(--warning-color-light-70)",
                        80: "var(--warning-color-light-80)",
                        90: "var(--warning-color-light-90)",
                    },
                },
                success: {
                    default:
                        "color-mix(in srgb, var(--success-color), transparent calc(100% - 100% * <alpha-value>))", // to enable opacity, see: https://github.com/tailwindlabs/tailwindcss/discussions/12622#discussioncomment-7909796
                    text: "var(--success-color-text)",
                    dark: {
                        10: "var(--success-color-dark-10)",
                        20: "var(--success-color-dark-20)",
                        30: "var(--success-color-dark-30)",
                        40: "var(--success-color-dark-40)",
                        50: "var(--success-color-dark-50)",
                        60: "var(--success-color-dark-60)",
                        70: "var(--success-color-dark-70)",
                        80: "var(--success-color-dark-80)",
                        90: "var(--success-color-dark-90)",
                    },
                    light: {
                        10: "var(--success-color-light-10)",
                        20: "var(--success-color-light-20)",
                        30: "var(--success-color-light-30)",
                        40: "var(--success-color-light-40)",
                        50: "var(--success-color-light-50)",
                        60: "var(--success-color-light-60)",
                        70: "var(--success-color-light-70)",
                        80: "var(--success-color-light-80)",
                        90: "var(--success-color-light-90)",
                    },
                },
                info: {
                    default: "var(--info-color)", // Base info color
                    text: "var(--info-color-text)",
                    dark: {
                        10: "var(--info-color-dark-10)",
                        20: "var(--info-color-dark-20)",
                        30: "var(--info-color-dark-30)",
                        40: "var(--info-color-dark-40)",
                        50: "var(--info-color-dark-50)",
                        60: "var(--info-color-dark-60)",
                        70: "var(--info-color-dark-70)",
                        80: "var(--info-color-dark-80)",
                        90: "var(--info-color-dark-90)",
                    },
                    light: {
                        10: "var(--info-color-light-10)",
                        20: "var(--info-color-light-20)",
                        30: "var(--info-color-light-30)",
                        40: "var(--info-color-light-40)",
                        50: "var(--info-color-light-50)",
                        60: "var(--info-color-light-60)",
                        70: "var(--info-color-light-70)",
                        80: "var(--info-color-light-80)",
                        90: "var(--info-color-light-90)",
                    },
                },
                surface: {
                    default:
                        "color-mix(in srgb, var(--surface-color), transparent calc(100% - 100% * <alpha-value>))", // Base surface color
                    1: "var(--surface-color-1)",
                    2: "var(--surface-color-2)",
                    3: "var(--surface-color-3)",
                    4: "var(--surface-color-4)",
                    5: "var(--surface-color-5)",
                    6: "var(--surface-color-6)",
                    7: "var(--surface-color-7)",
                    8: "var(--surface-color-8)",
                    9: "var(--surface-color-9)",
                    10: "var(--surface-color-10)",
                    20: "var(--surface-color-20)",
                    30: "var(--surface-color-30)",
                    40: "var(--surface-color-40)",
                    50: "var(--surface-color-50)",
                    60: "var(--surface-color-60)",
                    70: "var(--surface-color-70)",
                    80: "var(--surface-color-80)",
                    90: "var(--surface-color-90)",
                },
            },
            backgroundImage: {
                5154: "url('/images/5154.jpeg')",
            },
            backgroundColor: {
                neutral: "#313639",
            },
            animation: {
                "fade-in":
                    "fade-in 0.5s cubic-bezier(0.40, 0.6, 0.6, 1) 2s both",
                "bounce-top": "bounce-top 1s both",
                blink: "blink 2s ease-in-out infinite",
            },
            keyframes: {
                "fade-in": {
                    "0%": { opacity: 0 },
                    "100%": { opacity: 1 },
                },
                "bounce-top": {
                    "0%, 60%, 75%, 90%, 100%": {
                        "transition-timing-function":
                            "cubic-bezier(0.215, 0.610, 0.355, 1.000)",
                    },
                    "0%": {
                        opacity: 0,
                        transform: "translate3d(0, 30px, 0)",
                    },
                    "60%": {
                        opacity: 0,
                        transform: "translate3d(0, -15px, 0)",
                    },

                    "75%": {
                        transform: "translate3d(0, 10px, 0)",
                    },

                    "90%": {
                        transform: "translate3d(0, -5px, 0)",
                    },

                    "100%": {
                        transform: "none",
                    },
                },
                blink: {
                    "20%, 80%": { opacity: "1" },
                    "35%, 65%": { opacity: "0" },
                },
            },
        },
    },
    plugins: [
        ({ matchUtilities, theme /* … */ }) => {
            // …
            matchUtilities(
                // https://gist.github.com/olets/9b833a33d01384eed1e9f1e106003a3b
                {
                    aspect: (value) => ({
                        "@supports (aspect-ratio: 1 / 1)": {
                            aspectRatio: value,
                        },
                        "@supports not (aspect-ratio: 1 / 1)": {
                            // https://github.com/takamoso/postcss-aspect-ratio-polyfill

                            "&::before": {
                                content: '""',
                                float: "left",
                                paddingTop: `calc(100% / (${value}))`,
                            },
                            "&::after": {
                                clear: "left",
                                content: '""',
                                display: "block",
                            },
                        },
                    }),
                },
                { values: theme("aspectRatio") }
            );
        },
        require("@tailwindcss/forms"),
    ],
};
